import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { ScenarioAPI } from "../../utils/API/ScenarioAPI";
import {
  DELETE_SCENARIO,
  REFRESH_SCENARIO,
  SAVE_SCENARIO,
  START_SCENARIO,
  WS_COMPATIBILITY,
  WS_DATA_SCENARIO,
} from "../constants/scenario.constants";
import Scenario from "../../utils/models/Scenario";
import { selectOptions } from "../../containers/PresentationPage/selectors";
import { selectScenarios } from "../selectors/scenario.selector";
import { scenariosChanged } from "../actions/scenario.actions";
import { openSnackbar } from "../actions/snackbar.actions";
import * as Sentry from "@sentry/browser";
import { getI18n } from "react-i18next";
import { setOnMenuClicked } from "../actions/app.actions";
import {
  getScenarioTemplates,
  setIsLoading,
} from "../../containers/ScenariosPage/actions";
import {
  deleteV4ScenarioSaga,
  saveV4ScenarioSaga,
  startV4ScenariosSaga,
} from "./scenarioV4.saga";
import { isV4Uuid } from "../../containers/V4Scenarios/ids";

const api = new ScenarioAPI();

const isV4 = scenario =>
  !!(scenario?.isV4 || scenario?.get?.('source') === 'v4');

function* saveV3Scenario(scenario) {
  try {
    if (scenario.id) {
      yield api.updateScenario(scenario);
      // Mirror the saved scenario into the store so revisiting it (no page refresh) shows the new value.
      yield put({ type: WS_DATA_SCENARIO, data: scenario.toJS() });
    } else {
      yield api.newScenario(scenario);
    }
    return null;
  } catch (e) {
    // Uncaught, it would stop every saga watcher.
    // eslint-disable-next-line no-console
    console.warn("[V3 scenario save failed]", e);
    yield put(openSnackbar(getI18n().t("somethingWentWrong")));
    return { error: e };
  }
}
function* saveScenario({ scenario, callback }) {
  yield put(setIsLoading(true));
  let failure = null;
  try {
    // The dashboard toggle saves too, with no editor to show marks in.
    failure = isV4(scenario)
      ? yield* saveV4ScenarioSaga(scenario, { showsMarks: !!callback })
      : yield* saveV3Scenario(scenario);
  } finally {
    yield put(setIsLoading(false));
  }
  if (callback) callback(failure);
}
function* deleteScenario({ scenario }) {
  if (scenario == null) {
    Sentry.captureMessage("deleteScenario: 'scenario is null'");
    return;
  }
  yield put(setOnMenuClicked(null));
  if (isV4(scenario)) {
    yield* deleteV4ScenarioSaga(scenario);
    return;
  }
  const scenarios = yield select(selectScenarios());
  yield put(
    scenariosChanged(scenarios.filter((s) => !!s && s.id !== scenario.id))
  );
  yield api.deleteScenario(scenario);
  yield put(getScenarioTemplates());
}
function* refreshScenario({ scenario }) {
  // V3 /sec/scenario/{id} rejects UUIDs; V4 list refetches on save anyway.
  if (isV4(scenario)) return;
  yield api.refreshScenario(scenario);
}

function* saveCompatibility({ data }) {
  Scenario.compatibility = data;

  yield Promise.resolve();
}
function* startV3Scenarios(scenarioIds, payload) {
  try {
    let page = yield select(selectOptions());
    yield api.startScenario(scenarioIds, payload, page ? page.id : null);
  } catch (e) {
    // Uncaught, it would stop every saga watcher.
    // eslint-disable-next-line no-console
    console.warn("[V3 scenario start failed]", e);
  }
}
// A tile can mix V3 ids and V4 uuids.
function* startScenario({ scenarioId, payload }) {
  const ids = [].concat(scenarioId || []);
  const v3Ids = ids.filter((id) => !isV4Uuid(id));
  const v4Ids = ids.filter(isV4Uuid);
  yield all([
    ...(v3Ids.length ? [call(startV3Scenarios, v3Ids, payload)] : []),
    ...(v4Ids.length ? [call(startV4ScenariosSaga, v4Ids)] : []),
  ]);
}

export const scenarioSagas = [
  takeLatest(WS_COMPATIBILITY, saveCompatibility),
  takeLatest(SAVE_SCENARIO, saveScenario),
  takeLatest(DELETE_SCENARIO, deleteScenario),
  takeLatest(REFRESH_SCENARIO, refreshScenario),
  // takeLatest would cancel a V4 start still resolving its scope.
  takeEvery(START_SCENARIO, startScenario),
];
